<template>
  <div class="container-fluid">
    <!-- Bread-crumb-->
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>
    <div class="row clearfix">
      <div class="col-lg-12">
        <div class="card card-top-line">
          <!-- Header -->
          <div class="header d-flex justify-content-between align-items-center">
            <h2>File Upload - FX Rates</h2>
            <router-link to="/fx-rates" class="btn btn-outline-secondary">
              <i class="fa fa-arrow-left mr-2"></i>
              Back to FX Rates
            </router-link>
          </div>
          <!-- Main Content -->
          <div class="body pt-0">
            <div class="p-4">
              <!-- Drag & Drop Upload Section -->
              <div class="row">
                <div class="col-md-8">
                  <div class="form-group">
                    <label for="fileUpload">
                      Select File
                      <span class="text-danger">*</span>
                    </label>
                    <div
                      class="drag-drop-zone"
                      :class="{
                        'drag-over': isDragOver,
                        'has-file': selectedFile,
                      }"
                      @dragover.prevent="handleDragOver"
                      @dragleave.prevent="handleDragLeave"
                      @drop.prevent="handleDrop"
                      @click="triggerFileInput"
                    >
                      <div v-if="!selectedFile" class="drag-drop-content">
                        <i class="fa fa-cloud-upload fa-3x text-muted mb-3"></i>
                        <h5>Drag & Drop your file here</h5>
                        <p class="text-muted">or click to browse</p>
                        <small class="text-muted">
                          Supports CSV files (.csv)
                        </small>
                      </div>
                      <div v-else class="file-selected">
                        <i class="fa fa-file-text fa-2x text-success mb-2"></i>
                        <h6>{{ selectedFile.name }}</h6>
                        <p class="text-muted mb-0">
                          {{ formatFileSize(selectedFile.size) }}
                        </p>
                      </div>
                    </div>
                    <input
                      type="file"
                      class="d-none"
                      id="fileUpload"
                      @change="handleFileUpload"
                      accept=".csv"
                      ref="fileInput"
                    />
                    <small class="form-text text-muted">
                      Upload a CSV file with currency rates. File should contain
                      columns: source_currency, target_currency, rate, comments
                    </small>
                    <div v-if="errors.file" class="invalid-feedback d-block">
                      {{ errors.file }}
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label>&nbsp;</label>
                    <div class="d-flex flex-column">
                      <button
                        class="btn btn-success mb-2"
                        @click="downloadTemplate('csv')"
                        :disabled="isDownloading"
                      >
                        <i class="fa fa-download mr-2"></i>
                        {{
                          isDownloading
                            ? "Downloading..."
                            : "Download CSV Template"
                        }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- File Preview -->
              <div v-if="filePreview.length > 0" class="row mt-4">
                <div class="col-md-12">
                  <h5>File Preview (First 5 rows)</h5>
                  <div class="table-responsive">
                    <table class="table table-bordered table-sm">
                      <thead class="thead-light">
                        <tr>
                          <th>Source Currency</th>
                          <th>Target Currency</th>
                          <th>Rate</th>
                          <th>Comments</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, index) in filePreview"
                          :key="index"
                          :class="{ 'table-danger': row.errors.length > 0 }"
                        >
                          <td>{{ row.sourceCurrency }}</td>
                          <td>{{ row.targetCurrency }}</td>
                          <td>{{ formatRate(row.rate) }}</td>
                          <td>{{ row.comments }}</td>
                          <td>
                            <span
                              v-if="row.errors.length > 0"
                              class="badge badge-danger"
                            >
                              {{ row.errors.join(", ") }}
                            </span>
                            <span v-else class="badge badge-success">
                              Valid
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Upload Summary -->
              <div v-if="uploadSummary.total > 0" class="row mt-4">
                <div class="col-md-12">
                  <div
                    class="alert"
                    :class="
                      uploadSummary.errors > 0 ? 'alert-warning' : 'alert-info'
                    "
                  >
                    <h6>Upload Summary:</h6>
                    <ul class="mb-0">
                      <li>Total rows: {{ uploadSummary.total }}</li>
                      <li>Valid rows: {{ uploadSummary.valid }}</li>
                      <li v-if="uploadSummary.errors > 0">
                        Errors: {{ uploadSummary.errors }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Upload Results Table -->
              <div v-if="uploadResults" class="row mt-4">
                <div class="col-md-12">
                  <div class="card">
                    <div class="card-header">
                      <h5 class="mb-0">
                        <i class="fa fa-table mr-2"></i>
                        Upload Results
                        <span class="badge badge-success ml-2">
                          {{ uploadResults.summary.successCount }} Success
                        </span>
                        <span
                          v-if="uploadResults.summary.failureCount > 0"
                          class="badge badge-danger ml-2"
                        >
                          {{ uploadResults.summary.failureCount }} Failed
                        </span>
                      </h5>
                    </div>
                    <div class="card-body p-0">
                      <!-- Successes Table -->
                      <div
                        v-if="
                          uploadResults.successes &&
                          uploadResults.successes.length > 0
                        "
                        class="table-responsive"
                      >
                        <h6 class="px-3 pt-3 mb-2 text-success">
                          <i class="fa fa-check-circle mr-1"></i>
                          Successful Uploads ({{
                            uploadResults.successes.length
                          }})
                        </h6>
                        <table class="table table-sm table-hover mb-0">
                          <thead class="thead-light">
                            <tr>
                              <th>Row</th>
                              <th>Source Currency</th>
                              <th>Target Currency</th>
                              <th>Rate</th>
                              <th>Comments</th>
                              <th>Rate ID</th>
                              <th>Effective Date</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="success in uploadResults.successes"
                              :key="success.rateId"
                              class="table-success"
                            >
                              <td>
                                <span class="badge badge-light">
                                  {{ success.row }}
                                </span>
                              </td>
                              <td>
                                <strong>{{ success.sourceCurrency }}</strong>
                              </td>
                              <td>
                                <strong>{{ success.targetCurrency }}</strong>
                              </td>
                              <td>
                                <code>{{ formatRate(success.rate) }}</code>
                              </td>
                              <td>{{ success.comments || "-" }}</td>
                              <td>
                                <small class="text-muted">
                                  {{ success.rateId.substring(0, 8) }}...
                                </small>
                              </td>
                              <td>
                                <small>
                                  {{
                                    new Date(
                                      success.effectiveDate,
                                    ).toLocaleString()
                                  }}
                                </small>
                              </td>
                              <td>
                                <span class="badge badge-success">
                                  {{ success.status }}
                                </span>
                                <span
                                  v-if="success.previousRateDeleted"
                                  class="badge badge-info ml-1"
                                  title="Previous rate was replaced"
                                >
                                  Replaced
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Failures Table -->
                      <div
                        v-if="
                          uploadResults.failures &&
                          uploadResults.failures.length > 0
                        "
                        class="table-responsive"
                      >
                        <h6 class="px-3 pt-3 mb-2 text-danger">
                          <i class="fa fa-exclamation-triangle mr-1"></i>
                          Failed Uploads ({{ uploadResults.failures.length }})
                        </h6>
                        <table class="table table-sm table-hover mb-0">
                          <thead class="thead-light">
                            <tr>
                              <th>Row</th>
                              <th>Source Currency</th>
                              <th>Target Currency</th>
                              <th>Rate</th>
                              <th>Comments</th>
                              <th>Error Code</th>
                              <th>Error Message</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="failure in uploadResults.failures"
                              :key="failure.row"
                              class="table-danger"
                            >
                              <td>
                                <span class="badge badge-light">
                                  {{ failure.row }}
                                </span>
                              </td>
                              <td>{{ failure.input.source_currency }}</td>
                              <td>{{ failure.input.target_currency }}</td>
                              <td>{{ formatRate(failure.input.rate) }}</td>
                              <td>{{ failure.input.comments || "-" }}</td>
                              <td>
                                <code class="text-danger">
                                  {{ failure.code }}
                                </code>
                              </td>
                              <td>
                                <span class="text-danger">
                                  {{ failure.message }}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Summary Stats -->
                      <div class="p-3 bg-light border-top">
                        <div class="row text-center">
                          <div class="col-md-3">
                            <div class="stat-item">
                              <h4 class="text-primary mb-0">
                                {{ uploadResults.summary.totalRows }}
                              </h4>
                              <small class="text-muted">Total Rows</small>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="stat-item">
                              <h4 class="text-success mb-0">
                                {{ uploadResults.summary.successCount }}
                              </h4>
                              <small class="text-muted">Successful</small>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="stat-item">
                              <h4 class="text-danger mb-0">
                                {{ uploadResults.summary.failureCount }}
                              </h4>
                              <small class="text-muted">Failed</small>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="stat-item">
                              <h4 class="text-info mb-0">
                                {{
                                  Math.round(
                                    (uploadResults.summary.successCount /
                                      uploadResults.summary.totalRows) *
                                      100,
                                  )
                                }}%
                              </h4>
                              <small class="text-muted">Success Rate</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div v-if="!uploadResults" class="row mt-4">
                <div class="col-md-12">
                  <button
                    class="btn btn-primary"
                    @click="uploadFile"
                    :disabled="!selectedFile || isUploading"
                  >
                    <i class="fa fa-upload mr-2"></i>
                    {{ isUploading ? "Uploading..." : "Upload Rates" }}
                  </button>
                  <button
                    class="btn btn-secondary ml-2"
                    @click="clearFile"
                    :disabled="isUploading"
                  >
                    <i class="fa fa-times mr-2"></i>
                    Clear File
                  </button>
                </div>
              </div>

              <!-- Post-Upload Actions -->
              <div v-if="uploadResults" class="row mt-4">
                <div class="col-md-12">
                  <div
                    class="d-flex justify-content-between align-items-center"
                  >
                    <div class="text-success">
                      <i class="fa fa-check-circle mr-2"></i>
                      <strong>Upload completed successfully!</strong>
                    </div>
                    <div>
                      <router-link to="/fx-rates" class="btn btn-primary">
                        <i class="fa fa-arrow-left mr-2"></i>
                        Back to FX Rates
                      </router-link>
                      <button
                        class="btn btn-outline-secondary ml-2"
                        @click="clearFile"
                      >
                        <i class="fa fa-upload mr-2"></i>
                        Upload Another File
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@/plugins/sweetalert2";
import "@/plugins/vee-validate";

export default {
  name: "CsvUploadComponent",
  components: {
    BreadCrumb: () => import("@/components/BreadCrumb.vue"),
  },
  data() {
    return {
      selectedFile: null,
      filePreview: [],
      errors: {},
      isUploading: false,
      isDragOver: false,
      isDownloading: false,
      uploadSummary: {
        total: 0,
        valid: 0,
        errors: 0,
      },
      uploadResults: null,
    };
  },
  methods: {
    formatRate(rate) {
      if (rate === null || rate === undefined || rate === "") return "";
      return parseFloat(rate).toLocaleString(undefined, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
    },
    handleDragOver() {
      this.isDragOver = true;
    },

    handleDragLeave() {
      this.isDragOver = false;
    },

    handleDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (this.isValidFileType(file)) {
          this.selectedFile = file;
          this.errors.file = null;
          this.previewFile(file);
        } else {
          this.errors.file = "Please select a valid CSV file (.csv)";
        }
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file && this.isValidFileType(file)) {
        this.selectedFile = file;
        this.errors.file = null;
        this.previewFile(file);
      } else if (file) {
        this.errors.file = "Please select a valid CSV file (.csv)";
      }
    },

    isValidFileType(file) {
      const validTypes = ["text/csv"];
      const validExtensions = [".csv"];

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.errors.file = "File size must be less than 10MB";
        return false;
      }

      return (
        validTypes.includes(file.type) ||
        validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
      );
    },

    previewFile(file) {
      if (
        file.type === "text/csv" ||
        file.name.toLowerCase().endsWith(".csv")
      ) {
        this.previewCsvFile(file);
      } else {
        this.errors.file = "Only CSV files are supported";
      }
    },

    previewCsvFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csv = e.target.result;
          const lines = csv.split("\n");
          this.processFileData(lines);
        } catch (error) {
          this.errors.file =
            "Error processing CSV file. Please check the file format.";
          console.error("CSV processing error:", error);
        }
      };
      reader.onerror = () => {
        this.errors.file = "Error reading file. Please try again.";
      };
      reader.readAsText(file);
    },

    processFileData(lines) {
      this.filePreview = [];
      this.uploadSummary = {
        total: 0,
        valid: 0,
        errors: 0,
      };

      for (let i = 1; i < Math.min(6, lines.length); i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(",");
          const row = {
            sourceCurrency: values[0] || "",
            targetCurrency: values[1] || "",
            rate: values[2] || "",
            comments: values[3] || "",
            errors: [],
          };

          // Validate row
          if (!row.sourceCurrency) row.errors.push("Missing source currency");
          if (!row.targetCurrency) row.errors.push("Missing target currency");
          if (!row.rate || isNaN(row.rate)) row.errors.push("Invalid rate");
          if (row.sourceCurrency === row.targetCurrency)
            row.errors.push("Same currencies");
          // Comments field is optional, no validation needed

          this.filePreview.push(row);
          this.uploadSummary.total++;

          if (row.errors.length === 0) {
            this.uploadSummary.valid++;
          } else {
            this.uploadSummary.errors++;
          }
        }
      }
    },

    uploadFile() {
      if (!this.selectedFile) {
        this.errors.file = "Please select a file first";
        return;
      }

      this.isUploading = true;

      // Create FormData to send the file
      const formData = new FormData();
      formData.append("file", this.selectedFile);

      // Send file to fx-rates/upload-csv endpoint
      this.$http
        .post(
          "fx-rates/upload-csv",
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
              "Content-Type": "multipart/form-data",
            },
          },
        )
        .then((response) => {
          this.uploadResults = response.data;

          // Show success message with summary
          const successMessage = `File uploaded successfully!\n\nSummary:\n• Total rows: ${response.data.summary.totalRows}\n• Successful: ${response.data.summary.successCount}\n• Failed: ${response.data.summary.failureCount}`;

          this.$swal({
            title: "Upload Complete!",
            text: successMessage,
            icon: "success",
            confirmButtonText: "View Details",
          });
        })
        .catch((error) => {
          console.error("Upload error:", error);
          this.$swal({
            title: "Upload Failed",
            text: "There was an error uploading your file. Please try again.",
            icon: "error",
            confirmButtonText: "OK",
          });
        })
        .finally(() => {
          this.isUploading = false;
        });
    },

    downloadTemplate(type) {
      this.isDownloading = true;

      if (type === "csv") {
        // Create and download CSV template matching your existing structure
        const csvContent =
          "source_currency,target_currency,rate,comments\nUSD,GBP,1550.25,August rate\nUSD,GHS,12.3456789,Promo pricing\nUSD,KES,128.5,\nUSD,ZAR,17.75,Auto-approved via CSV";
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "FXRatesTemplate.csv";
        a.click();
        window.URL.revokeObjectURL(url);

        // Show success message
        this.$swal({
          title: "Download Complete!",
          text: "CSV template has been downloaded successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }

      // Reset downloading state after a short delay
      setTimeout(() => {
        this.isDownloading = false;
      }, 1000);
    },

    clearFile() {
      this.selectedFile = null;
      this.filePreview = [];
      this.errors = {};
      this.uploadSummary = {
        total: 0,
        valid: 0,
        errors: 0,
      };
      this.uploadResults = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.drag-drop-zone {
  border: 2px dashed #dee2e6;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.drag-drop-zone::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transition: left 0.5s;
}

.drag-drop-zone:hover::before {
  left: 100%;
}

.drag-drop-zone:hover {
  border-color: #007bff;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.15);
  transform: translateY(-2px);
}

.drag-drop-zone.drag-over {
  border-color: #007bff;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  transform: scale(1.02);
  box-shadow: 0 12px 30px rgba(0, 123, 255, 0.2);
}

.drag-drop-zone.has-file {
  border-color: #28a745;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.15);
}

.drag-drop-content {
  color: #6c757d;
  z-index: 1;
  position: relative;
}

.drag-drop-content i {
  color: #adb5bd;
  transition: color 0.3s ease;
}

.drag-drop-zone:hover .drag-drop-content i {
  color: #007bff;
}

.file-selected {
  color: #155724;
  z-index: 1;
  position: relative;
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-control-file.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #dc3545;
}

.table-danger {
  background-color: #f8d7da;
}

.btn {
  transition: all 0.3s ease;
  border-radius: 8px;
  font-weight: 500;
  padding: 10px 20px;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
}

.btn:active {
  transform: translateY(0);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.btn:disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}

.btn-primary {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
}

.btn-success {
  background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
  border: none;
}

.btn-success:hover {
  background: linear-gradient(135deg, #1e7e34 0%, #155724 100%);
}

.btn-info {
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  border: none;
}

.btn-info:hover {
  background: linear-gradient(135deg, #138496 0%, #0c5460 100%);
}

/* Upload Results Table Styles */
.table-success {
  background-color: #d4edda !important;
}

.table-danger {
  background-color: #f8d7da !important;
}

.stat-item {
  padding: 10px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.stat-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
}

.stat-item h4 {
  font-weight: 600;
  margin-bottom: 5px;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
}

.badge-light {
  background-color: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

code {
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #e83e8c;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .drag-drop-zone {
    min-height: 150px;
    padding: 30px 15px;
  }

  .drag-drop-content h5 {
    font-size: 1.1rem;
  }

  .drag-drop-content p {
    font-size: 0.9rem;
  }

  .btn {
    padding: 8px 16px;
    font-size: 0.9rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .stat-item h4 {
    font-size: 1.5rem;
  }
}
</style>
